/**
 * CustomerModal — unified create / edit / view modal for customers.
 *
 * Replaces the previous inline edit dialog (in CustomersPage) AND the
 * separate read-only CustomerDetailModal. Single source of truth.
 *
 * Tabs:
 *   Details    — Name, phone, email, service type, notes  (always editable)
 *   Addresses  — Multi-address list with per-row add/edit/default/delete
 *                (persisted immediately via /api/customers/{id}/addresses)
 *   Jobs       — Total/spent/active counters + per-job cards
 *   Activity   — Created date, last call, last SMS, portal status
 *
 * Behavior:
 *   • For "Create" mode (customer === null): only Details + Addresses tabs
 *     are visible, and addresses are deferred until after the customer
 *     is saved (since we need a customer_id to persist them).
 *   • For "Edit" mode: all four tabs visible; per-row address ops persist
 *     immediately; the Save button batches Details changes (name/phone/
 *     email/notes/service_type/address+city).
 *
 * Responsive:
 *   • Desktop: centered modal, max-w 760px, max-h 90vh.
 *   • Mobile (<sm breakpoint): full-screen, sticky bottom action bar.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import StateProvinceSelect from "../StateProvinceSelect";
import { formatPostal } from "../../utils/addressUtils";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Phone, Mail, MapPin, MessageSquare, User, Plus, Trash2, Loader2,
  Briefcase, Calendar, DollarSign, FileText, Wrench, Users, Clock,
  CreditCard, Star, Check, Home, X, Paperclip, ChevronRight, Download,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { API } from "../../App";
import { formatPhoneInput } from "../../utils/phoneUtils";
import { formatScheduledTime, extractTimeFromDate } from "../../utils/dateUtils";
import { attachmentUrl, isImage, isMissing, formatFileSize, openAttachment, downloadAttachment, AttachmentLightbox } from "../jobs/JobAttachments";

const SERVICE_TYPES = [
  { value: "locksmith", label: "Locksmith" },
  { value: "garage_door", label: "Garage Door" },
  { value: "carpet_cleaning", label: "Carpet Cleaning" },
  { value: "hvac", label: "HVAC" },
  { value: "plumbing", label: "Plumbing" },
  { value: "electrical", label: "Electrical" },
  { value: "cleaning", label: "Cleaning" },
  { value: "chimney", label: "Chimney" },
  { value: "other", label: "Other" },
];

const ADDRESS_LABEL_PRESETS = ["Home", "Office", "Service location", "Billing", "Other"];

const statusConfig = {
  completed: { label: "Completed", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  completed_pending_payment: { label: "Pending payment", color: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  deposit_collected: { label: "Deposit collected", color: "bg-teal-500/15 text-teal-400 border-teal-500/30" },
  scheduled: { label: "Scheduled", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  in_progress: { label: "In progress", color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  cancelled: { label: "Cancelled", color: "bg-red-500/15 text-red-400 border-red-500/30" },
  pending: { label: "Pending", color: "bg-gray-500/15 text-gray-400 border-gray-500/30" },
};

const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  secondary_phone: "",
  service_type: "",
  notes: "",
  // Legacy single-address fields — kept for backwards-compat with existing rows.
  address: "",
  city: "",
  state: "",
  zip_code: "",
  // Tax exemption
  tax_exempt: false,
  tax_exempt_type: "",
  tax_exempt_certificate: "",
  tax_exempt_expires: "",
  // Free-form key/value custom fields (e.g. imported from Jobber)
  custom_fields_rows: [],
};

const CustomerModal = ({ open, onOpenChange, customer, defaultTab, onSaved, onDeleted, onOpenJob, onCreateJob, elevated = false }) => {
  const isCreate = !customer?.customer_id;

  const [tab, setTab] = useState(defaultTab || "details");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Business type from Settings → Business Settings. Drives the "Suggested"
  // option at the top of the Service Type select and the default for new rows.
  const [businessType, setBusinessType] = useState(null);

  useEffect(() => {
    if (!open || businessType !== null) return;
    axios
      .get(`${API}/settings/business`, auth())
      .then((res) => setBusinessType(res.data?.business_type || "other"))
      .catch(() => setBusinessType("other"));
  }, [open, businessType]);

  // Known preset value for the business type ("custom" → "other")
  const suggestedServiceType = !businessType
    ? null
    : SERVICE_TYPES.find((s) => s.value === businessType)
    ? businessType
    : "other";

  // Suggested option first, rest after (Other always last)
  const serviceOptions = suggestedServiceType
    ? [
        SERVICE_TYPES.find((s) => s.value === suggestedServiceType),
        ...SERVICE_TYPES.filter((s) => s.value !== suggestedServiceType),
      ]
    : SERVICE_TYPES;

  // ── Hydrate form when a customer is opened
  useEffect(() => {
    if (!open) return;
    setTab(defaultTab || "details");
    setDirty(false);
    if (customer) {
      setForm({
        name: customer.name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        secondary_phone: customer.secondary_phone || "",
        service_type: customer.service_type || "",
        notes: customer.notes || "",
        address: customer.address || "",
        city: customer.city || "",
        state: customer.state || "",
        zip_code: customer.zip_code || "",
        tax_exempt: customer.tax_exempt === true,
        tax_exempt_type: customer.tax_exempt_type || "",
        tax_exempt_certificate: customer.tax_exempt_certificate || "",
        tax_exempt_expires: customer.tax_exempt_expires || "",
        custom_fields_rows: Object.entries(customer.custom_fields || {}).map(
          ([k, v]) => ({ key: k, value: String(v ?? "") })
        ),
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, customer, defaultTab]);

  // Default empty service type to the business's own type once settings load
  useEffect(() => {
    if (!open || !suggestedServiceType) return;
    setForm((f) => (f.service_type ? f : { ...f, service_type: suggestedServiceType }));
  }, [open, suggestedServiceType, customer]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };

  const handleSave = async (closeAfter = true) => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      custom_fields: Object.fromEntries(
        (form.custom_fields_rows || [])
          .filter((r) => r.key.trim())
          .map((r) => [r.key.trim(), r.value])
      ),
    };
    delete payload.custom_fields_rows;
    try {
      if (isCreate) {
        const res = await axios.post(`${API}/customers`, payload, auth());
        toast.success("Customer added");
        onSaved?.(res.data, true);
        if (closeAfter) onOpenChange(false);
      } else {
        const res = await axios.put(`${API}/customers/${customer.customer_id}`, payload, auth());
        toast.success("Customer updated");
        onSaved?.(res.data, false);
        setDirty(false);
        if (closeAfter) onOpenChange(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!customer?.customer_id) return;
    if (!window.confirm(`Delete ${customer.name}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/customers/${customer.customer_id}`, auth());
      toast.success("Customer deleted");
      onDeleted?.(customer.customer_id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const initial = (form.name || customer?.name || "?").charAt(0).toUpperCase();
  const tabsToShow = isCreate
    ? ["details", "addresses"]
    : ["details", "addresses", "jobs", "files", "activity"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Full-screen on mobile, modal on desktop. The trick: zero-out the
        // shadcn defaults (rounded, translate) on sm-, then re-apply at sm+.
        className={`
          p-0 gap-0 bg-background border-border
          w-screen h-[100dvh] max-w-none max-h-[100dvh] rounded-none translate-x-0 translate-y-0 top-0 left-0
          sm:w-[95vw] sm:h-auto sm:max-w-[760px] sm:max-h-[90vh] sm:rounded-2xl sm:top-[50%] sm:left-[50%] sm:-translate-x-1/2 sm:-translate-y-1/2
          flex flex-col overflow-hidden
          ${elevated ? "!z-[70]" : ""}
        `}
        data-testid="customer-modal"
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-border space-y-3 flex-shrink-0">
          <DialogHeader className="space-y-0">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-[#0046BE]/15 border border-[#0046BE]/30 flex items-center justify-center flex-shrink-0">
                <span className="text-[#0046BE] font-bold text-lg">{initial}</span>
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-foreground font-['Manrope'] text-lg leading-tight truncate">
                  {isCreate ? "Add new customer" : form.name || customer?.name}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground text-xs mt-0.5">
                  {isCreate
                    ? "Fill in the details below — addresses, jobs and activity unlock once saved."
                    : `Customer since ${customer?.created_at ? format(new Date(customer.created_at), "MMM d, yyyy") : "—"}`}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Quick actions — only when editing an existing customer */}
          {!isCreate && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="customer-modal-quick-actions">
              <QuickAction icon={Phone} label="Call" href={customer?.phone ? `tel:${customer.phone}` : null} testid="quick-call" />
              <QuickAction icon={MessageSquare} label="SMS" href={customer?.phone ? `sms:${customer.phone}` : null} testid="quick-sms" />
              <QuickAction icon={Mail} label="Email" href={customer?.email ? `mailto:${customer.email}` : null} testid="quick-email" />
            </div>
          )}

          {/* Tabs */}
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className={`w-full grid bg-theme-surface h-9 ${tabsToShow.length === 2 ? "grid-cols-2" : "grid-cols-5"}`}>
              {tabsToShow.map((t) => (
                <TabsTrigger
                  key={t}
                  value={t}
                  className="text-xs data-[state=active]:bg-[#0046BE] data-[state=active]:text-white"
                  data-testid={`customer-tab-${t}`}
                >
                  {{ details: "Details", addresses: "Addresses", jobs: "Jobs", files: "Files", activity: "Activity" }[t]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* ── Body ───────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 space-y-4">
          {tab === "details" && (
            <DetailsTab form={form} update={update} serviceOptions={serviceOptions} suggestedServiceType={suggestedServiceType} />
          )}
          {tab === "addresses" && (
            isCreate ? (
              <div className="rounded-lg border border-dashed border-border bg-theme-surface p-6 text-center text-sm text-muted-foreground" data-testid="addresses-locked-msg">
                <MapPin className="w-6 h-6 mx-auto mb-2 opacity-40" />
                Save the customer first to start adding addresses. The first address line below will be saved as the primary location.
              </div>
            ) : (
              <AddressesTab customer={customer} />
            )
          )}
          {tab === "jobs" && !isCreate && (
            <JobsTab customer={customer} onOpenJob={onOpenJob} onCreateJob={onCreateJob} />
          )}
          {tab === "files" && !isCreate && (
            <FilesTab customer={customer} onOpenJob={onOpenJob} />
          )}
          {tab === "activity" && !isCreate && (
            <ActivityTab customer={customer} />
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <div className="border-t border-border bg-background/95 backdrop-blur px-4 sm:px-6 py-3 flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            {!isCreate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                disabled={saving}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid="customer-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="text-muted-foreground"
              data-testid="customer-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={saving || (!isCreate && !dirty)}
              className="btn-primary"
              data-testid="customer-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isCreate ? "Add customer" : "Save changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Quick action button ─────────────────────────────────────────

const QuickAction = ({ icon: Icon, label, href, testid }) => {
  if (!href) {
    return (
      <Button size="sm" variant="outline" disabled className="opacity-50" data-testid={testid}>
        <Icon className="w-3.5 h-3.5 mr-1.5" /> {label}
      </Button>
    );
  }
  return (
    <a href={href} className="inline-flex">
      <Button size="sm" variant="outline" data-testid={testid}>
        <Icon className="w-3.5 h-3.5 mr-1.5" /> {label}
      </Button>
    </a>
  );
};

// ─── Tab: Details ────────────────────────────────────────────────

const DetailsTab = ({ form, update, serviceOptions = SERVICE_TYPES, suggestedServiceType = null }) => {
  // Inline address autocomplete (Google Places via /api/places/autocomplete)
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);
  const boxRef = useRef(null);

  const onAddressChange = useCallback((value) => {
    update({ address: value });
    if (debounce.current) clearTimeout(debounce.current);
    if (value.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/places/autocomplete?input=${encodeURIComponent(value)}`,
          auth(),
        );
        const list = res.data?.predictions || res.data?.suggestions || [];
        setSuggestions(list);
        setOpen(list.length > 0);
      } catch {
        setSuggestions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickSuggestion = async (s) => {
    // Use the description as a sensible fallback even if /places/details fails.
    update({ address: s.description || s.text || "" });
    setOpen(false);
    setSuggestions([]);
    // If Google gave us a place_id, fetch structured components so we can
    // auto-fill city / state / zip in the same form — saves the user from
    // typing them in 4 separate boxes.
    if (s.place_id) {
      try {
        const res = await axios.get(
          `${API}/places/details/${encodeURIComponent(s.place_id)}`,
          auth(),
        );
        const d = res.data || {};
        // Prefer the structured street if present; falls back to whatever
        // is already in the address field.
        update({
          address: d.street || s.description || s.text || "",
          city: d.city || "",
          state: d.state || "",
          zip_code: d.zip || d.zip_code || "",
        });
      } catch {
        /* keep the freeform description we already wrote */
      }
    }
  };

  return (
    <div className="space-y-4" data-testid="customer-details-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name" required>
          <Input
            value={form.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Customer name"
            className="input-glass"
            data-testid="customer-name-input"
          />
        </Field>
        <Field label="Phone" required>
          <Input
            value={form.phone}
            onChange={(e) => update({ phone: formatPhoneInput(e.target.value) })}
            placeholder="(555) 123-4567"
            className="input-glass"
            data-testid="customer-phone-input"
          />
        </Field>
        <Field label="Secondary phone">
          <Input
            value={form.secondary_phone}
            onChange={(e) => update({ secondary_phone: formatPhoneInput(e.target.value) })}
            placeholder="(555) 987-6543 (optional)"
            className="input-glass"
            data-testid="customer-secondary-phone-input"
          />
        </Field>
      </div>

      <Field label="Email">
        <Input
          type="email"
          value={form.email}
          onChange={(e) => update({ email: e.target.value })}
          placeholder="email@example.com"
          className="input-glass"
          data-testid="customer-email-input"
        />
      </Field>

      <Field label="Service type" required>
        <Select value={form.service_type} onValueChange={(v) => update({ service_type: v })}>
          <SelectTrigger className="input-glass" data-testid="customer-service-select">
            <SelectValue placeholder="Select service type" />
          </SelectTrigger>
          <SelectContent>
            {serviceOptions.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`customer-service-option-${s.value}`}>
                {s.value === suggestedServiceType ? (
                  <span className="flex items-center gap-2">
                    {s.label}
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[#FFCC00]">Suggested</span>
                  </span>
                ) : (
                  s.label
                )}
              </SelectItem>
            ))}
            {/* Show the current value if it isn't in our preset list so
                legacy customers don't appear to have a blank field */}
            {form.service_type && !SERVICE_TYPES.find((s) => s.value === form.service_type) && (
              <SelectItem value={form.service_type}>
                {form.service_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </Field>

      <div className="rounded-xl border border-border bg-theme-surface p-3.5 space-y-3" data-testid="customer-tax-exempt-section">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm text-foreground">Tax Exempt Customer</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">Tax automatically becomes $0 on this customer's jobs (jurisdiction is still recorded)</p>
          </div>
          <Switch checked={form.tax_exempt === true} onCheckedChange={(v) => update({ tax_exempt: v })} data-testid="customer-tax-exempt-switch" />
        </div>
        {form.tax_exempt === true && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="customer-tax-exempt-fields">
            <Field label="Exemption type">
              <Select value={form.tax_exempt_type || "other"} onValueChange={(v) => update({ tax_exempt_type: v })}>
                <SelectTrigger className="input-glass" data-testid="customer-tax-exempt-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="government">Government</SelectItem>
                  <SelectItem value="nonprofit">Nonprofit</SelectItem>
                  <SelectItem value="resale">Resale</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Certificate / reference #">
              <Input value={form.tax_exempt_certificate} onChange={(e) => update({ tax_exempt_certificate: e.target.value })}
                className="input-glass" placeholder="Certificate number" data-testid="customer-tax-exempt-cert" />
            </Field>
            <Field label="Expires (optional)">
              <Input type="date" value={form.tax_exempt_expires} onChange={(e) => update({ tax_exempt_expires: e.target.value })}
                className="input-glass" data-testid="customer-tax-exempt-expires" />
            </Field>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Address" hint="The primary service location">
          <div className="relative" ref={boxRef}>
            <Input
              value={form.address}
              onChange={(e) => onAddressChange(e.target.value)}
              onBlur={() => setTimeout(() => setOpen(false), 200)}
              placeholder="Start typing an address..."
              className="input-glass"
              autoComplete="off"
              data-testid="customer-address-input"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground/60" />
              </div>
            )}
            {open && suggestions.length > 0 && (
              <div
                className="absolute z-[9999] top-full mt-1 w-full bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto overscroll-contain"
                style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                data-testid="address-suggestions"
              >
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    onMouseDown={() => pickSuggestion(s)}
                    data-testid={`address-suggestion-${i}`}
                  >
                    {s.description || s.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Field>
        <Field label="City">
          <Input
            value={form.city}
            onChange={(e) => update({ city: e.target.value })}
            placeholder="City"
            className="input-glass"
            data-testid="customer-city-input"
          />
        </Field>
      </div>

      {/* State + Zip — kept separate from the street/city row above so all
          four fields are easily visible on mobile. Filled automatically
          when the user picks an address suggestion. */}
      <div className="grid grid-cols-2 gap-3">
        <Field label="State / Province">
          <StateProvinceSelect
            value={form.state}
            onChange={(v) => update({ state: v })}
            className="input-glass"
            data-testid="customer-state-input"
          />
        </Field>
        <Field label="Zip / Postal code">
          <Input
            value={form.zip_code}
            onChange={(e) => update({ zip_code: formatPostal(e.target.value) })}
            placeholder="78701 or A1A 1A1"
            className="input-glass"
            data-testid="customer-zip-input"
          />
        </Field>
      </div>

      <Field label="Notes" hint="Anything your team should know — gate codes, dog warnings, etc.">
        <Textarea
          value={form.notes}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Additional notes..."
          className="input-glass min-h-[88px]"
          data-testid="customer-notes-input"
        />
      </Field>

      {/* Custom fields — free-form key/value (e.g. Hook#, PO requirements, Jobber imports) */}
      <div className="rounded-xl border border-border bg-theme-surface p-3.5 space-y-2.5" data-testid="customer-custom-fields-section">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm text-foreground">Custom fields</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Extra details like Hook#, Rolodex location, or PO requirements
            </p>
          </div>
          <Button
            type="button" size="sm" variant="outline"
            onClick={() => update({ custom_fields_rows: [...(form.custom_fields_rows || []), { key: "", value: "" }] })}
            data-testid="customer-add-custom-field-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add field
          </Button>
        </div>
        {(form.custom_fields_rows || []).map((row, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`customer-custom-field-row-${i}`}>
            <Input
              value={row.key}
              onChange={(e) => {
                const rows = [...form.custom_fields_rows];
                rows[i] = { ...rows[i], key: e.target.value };
                update({ custom_fields_rows: rows });
              }}
              placeholder="Field name"
              className="input-glass flex-1"
              data-testid={`customer-custom-field-key-${i}`}
            />
            <Input
              value={row.value}
              onChange={(e) => {
                const rows = [...form.custom_fields_rows];
                rows[i] = { ...rows[i], value: e.target.value };
                update({ custom_fields_rows: rows });
              }}
              placeholder="Value"
              className="input-glass flex-1"
              data-testid={`customer-custom-field-value-${i}`}
            />
            <Button
              type="button" size="icon" variant="ghost"
              className="shrink-0 text-muted-foreground hover:text-red-400"
              onClick={() => update({ custom_fields_rows: form.custom_fields_rows.filter((_, j) => j !== i) })}
              data-testid={`customer-custom-field-remove-${i}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, hint, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-muted-foreground text-xs">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </Label>
    {children}
    {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
  </div>
);

// ─── Tab: Addresses ──────────────────────────────────────────────

const AddressesTab = ({ customer }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newAddr, setNewAddr] = useState({
    address: "", city: "", state: "", zip_code: "", label: "Service location", is_default: false,
  });

  const load = useCallback(async () => {
    if (!customer?.customer_id) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/customers/${customer.customer_id}/addresses`,
        auth(),
      );
      setAddresses(res.data?.addresses || []);
    } catch {
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => { load(); }, [load]);

  const addAddress = async () => {
    if (!newAddr.address.trim()) {
      toast.error("Address line is required");
      return;
    }
    setAdding(true);
    try {
      await axios.post(
        `${API}/customers/${customer.customer_id}/addresses`,
        newAddr,
        auth(),
      );
      toast.success("Address added");
      setNewAddr({ address: "", city: "", state: "", zip_code: "", label: "Service location", is_default: false });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add address");
    } finally {
      setAdding(false);
    }
  };

  const removeAddress = async (address_id) => {
    if (!window.confirm("Remove this address?")) return;
    try {
      await axios.delete(
        `${API}/customers/${customer.customer_id}/addresses/${address_id}`,
        auth(),
      );
      toast.success("Address removed");
      load();
    } catch {
      toast.error("Failed to remove address");
    }
  };

  return (
    <div className="space-y-3" data-testid="customer-addresses-tab">
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading addresses…
        </div>
      ) : addresses.length === 0 && !showForm ? (
        <div className="rounded-lg border border-dashed border-border bg-theme-surface p-6 text-center text-sm text-muted-foreground">
          <MapPin className="w-6 h-6 mx-auto mb-2 opacity-40" />
          <p>No addresses on file yet.</p>
          <Button
            size="sm"
            className="btn-primary mt-3"
            onClick={() => setShowForm(true)}
            data-testid="add-first-address-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add address
          </Button>
        </div>
      ) : (
        <>
          {addresses.map((a) => (
            <div
              key={a.address_id}
              className="rounded-lg border border-border bg-theme-surface p-3"
              data-testid={`address-${a.address_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    <span className="text-xs font-semibold text-foreground">{a.label || "Service location"}</span>
                    {a.is_default && (
                      <Badge variant="outline" className="text-[10px] text-amber-400 bg-amber-500/10 border-amber-500/30">
                        <Star className="w-2.5 h-2.5 mr-0.5 fill-current" /> Default
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-foreground truncate">{a.full_address || a.address_line1}</p>
                  {a.notes && <p className="text-[11px] text-muted-foreground/70 mt-1">{a.notes}</p>}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0"
                  onClick={() => removeAddress(a.address_id)}
                  data-testid={`remove-address-${a.address_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {!showForm && (
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={() => setShowForm(true)}
              data-testid="add-address-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add another address
            </Button>
          )}
        </>
      )}

      {showForm && (
        <div className="rounded-lg border border-[#0046BE]/30 bg-[#0046BE]/5 p-3 space-y-3" data-testid="new-address-form">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-foreground">New address</h4>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setShowForm(false)}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Label">
              <Select
                value={newAddr.label}
                onValueChange={(v) => setNewAddr((s) => ({ ...s, label: v }))}
              >
                <SelectTrigger className="input-glass h-9" data-testid="new-address-label">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADDRESS_LABEL_PRESETS.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Street" required>
              <Input
                value={newAddr.address}
                onChange={(e) => setNewAddr((s) => ({ ...s, address: e.target.value }))}
                placeholder="123 Main St"
                className="input-glass h-9"
                data-testid="new-address-street"
              />
            </Field>
            <Field label="City">
              <Input
                value={newAddr.city}
                onChange={(e) => setNewAddr((s) => ({ ...s, city: e.target.value }))}
                placeholder="City"
                className="input-glass h-9"
                data-testid="new-address-city"
              />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="State / Province">
                <StateProvinceSelect
                  value={newAddr.state}
                  onChange={(v) => setNewAddr((s) => ({ ...s, state: v }))}
                  className="input-glass h-9"
                  data-testid="new-address-state"
                />
              </Field>
              <Field label="Zip / Postal">
                <Input
                  value={newAddr.zip_code}
                  onChange={(e) => setNewAddr((s) => ({ ...s, zip_code: formatPostal(e.target.value) }))}
                  placeholder="95814 or A1A 1A1"
                  className="input-glass h-9"
                  data-testid="new-address-zip"
                />
              </Field>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={newAddr.is_default}
              onChange={(e) => setNewAddr((s) => ({ ...s, is_default: e.target.checked }))}
              data-testid="new-address-default"
              className="rounded border-border"
            />
            Set as default address
          </label>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)} disabled={adding}>
              Cancel
            </Button>
            <Button size="sm" onClick={addAddress} disabled={adding} className="btn-primary" data-testid="save-new-address-btn">
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              Save address
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Jobs ───────────────────────────────────────────────────

const JobsTab = ({ customer, onOpenJob, onCreateJob }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  // payment_id → name map so native job payments (payment_type_id) display properly
  const [ptMap, setPtMap] = useState({});

  useEffect(() => {
    if (!customer?.customer_id) return;
    setLoading(true);
    axios
      .get(`${API}/customers/${customer.customer_id}/jobs`, auth())
      .then((res) => setJobs(res.data || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [customer]);

  useEffect(() => {
    axios.get(`${API}/settings/payment-types`, auth())
      .then((res) => {
        const m = {};
        (Array.isArray(res.data) ? res.data : []).forEach((pt) => { m[pt.payment_id] = pt.name; });
        setPtMap(m);
      })
      .catch(() => {});
  }, []);

  const totalEarned = jobs.reduce((s, j) => s + (j.total_charged || 0), 0);
  const activeJobs = jobs.filter((j) => ["scheduled", "in_progress", "pending", "deposit_collected"].includes(j.status));

  return (
    <div className="space-y-3" data-testid="customer-jobs-tab">
      {/* Quick action — create a job prefilled with this customer */}
      {onCreateJob && customer?.customer_id && (
        <Button
          size="sm"
          className="w-full bg-[#0046BE] hover:bg-[#0046BE]/90 text-white"
          onClick={() => onCreateJob(customer)}
          data-testid="customer-create-job-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Create Job for {customer?.name?.split(" ")[0] || "Customer"}
        </Button>
      )}

      {/* Counters */}
      <div className="grid grid-cols-3 gap-2">
        <Counter label="Total jobs" value={jobs.length} />
        <Counter label="Lifetime spent" value={`$${totalEarned.toLocaleString()}`} accent="emerald" />
        <Counter label="Active" value={activeJobs.length} accent="blue" />
      </div>

      {/* Jobs list */}
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading job history…
        </div>
      ) : jobs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-theme-surface p-6 text-center text-sm text-muted-foreground">
          <FileText className="w-6 h-6 mx-auto mb-2 opacity-40" />
          No jobs for this customer yet.
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map((j) => (
            <JobRow key={j.job_id} job={j} ptMap={ptMap} onOpen={onOpenJob ? () => onOpenJob(j) : undefined} />
          ))}
        </div>
      )}
    </div>
  );
};

const Counter = ({ label, value, accent }) => {
  const accentClasses = {
    emerald: "text-emerald-400",
    blue: "text-blue-400",
  };
  return (
    <div className="rounded-lg bg-theme-surface border border-border p-2.5 text-center">
      <p className={`text-lg font-bold ${accentClasses[accent] || "text-foreground"}`}>{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
};

const JobRow = ({ job, ptMap = {}, onOpen }) => {
  const cfg = statusConfig[job.status] || statusConfig.pending;
  const fmtDate = () => {
    try { return format(new Date(job.scheduled_date), "MMM d, yyyy"); } catch { return "—"; }
  };
  const fmtTime = () => {
    if (job.scheduled_time) return formatScheduledTime(job.scheduled_time);
    return extractTimeFromDate(job.scheduled_date) || "";
  };
  // Deposits / partial payments — payments can live on the job OR its invoice.
  const jobPaid = (job.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const paid = Math.max(jobPaid, Number(job.invoice_paid) || 0);
  const total = Number(job.total_charged) || Number(job.invoice_total) || 0;
  const isPartial = paid > 0 && total > 0 && paid < total - 0.009;
  const isDeposit = job.status === "deposit_collected";
  const paymentNames = (job.payments || [])
    .map((p) => ptMap[p.payment_type_id] || p.type || p.method)
    .filter(Boolean)
    .join(", ");
  return (
    <div
      className={`rounded-lg border border-border bg-theme-surface p-3 ${onOpen ? "cursor-pointer hover:border-[#0046BE]/50 hover:bg-theme-hover transition-colors group" : ""}`}
      onClick={onOpen}
      role={onOpen ? "button" : undefined}
      data-testid={`job-row-${job.job_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-semibold text-foreground truncate flex items-center gap-1">
          {job.title || "Untitled job"}
          {onOpen && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-[#0046BE] shrink-0" />}
        </p>
        <Badge variant="outline" className={`${cfg.color} text-[10px] flex-shrink-0`}>{cfg.label}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <Inline icon={Calendar}>{fmtDate()}</Inline>
        <Inline icon={Clock}>{fmtTime() || "N/A"}</Inline>
        <Inline icon={Users}>{job.technician_name || "Unassigned"}</Inline>
        <Inline icon={DollarSign} highlight={total > 0}>
          {total > 0 ? `$${Number(total).toLocaleString()}` : "—"}
        </Inline>
        {job.customer_address && (
          <Inline icon={MapPin} fullWidth>{job.customer_address}</Inline>
        )}
        {paymentNames && (
          <Inline icon={CreditCard} fullWidth>{paymentNames}</Inline>
        )}
        {(isPartial || (isDeposit && paid > 0)) && (
          <div
            className="col-span-2 flex items-center gap-1.5 rounded-md bg-amber-500/10 border border-amber-500/25 px-2 py-1"
            data-testid={`job-paid-balance-${job.job_id}`}
          >
            <CreditCard className="w-3 h-3 text-amber-500 flex-shrink-0" />
            <span className="text-amber-600 font-medium">
              {isDeposit ? "Deposit paid" : "Paid"} ${paid.toFixed(2)} · Balance ${Math.max(total - paid, 0).toFixed(2)}
            </span>
          </div>
        )}
        {(job.parts || []).length > 0 && (
          <Inline icon={Wrench} fullWidth>
            {(job.parts || []).map((p) => p.name).join(", ")}
          </Inline>
        )}
      </div>
    </div>
  );
};

const Inline = ({ icon: Icon, children, highlight, fullWidth }) => (
  <div className={`flex items-center gap-1.5 min-w-0 ${fullWidth ? "col-span-2" : ""}`}>
    <Icon className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />
    <span className={`truncate ${highlight ? "text-emerald-400 font-semibold" : "text-foreground"}`}>{children}</span>
  </div>
);

// ─── Tab: Files ──────────────────────────────────────────────────

const FilesTab = ({ customer, onOpenJob }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxAtt, setLightboxAtt] = useState(null);

  useEffect(() => {
    if (!customer?.customer_id) return;
    setLoading(true);
    axios
      .get(`${API}/customers/${customer.customer_id}/attachments`, auth())
      .then((res) => setFiles(Array.isArray(res.data) ? res.data : []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [customer]);

  const fmtDate = (iso) => {
    try { return format(new Date(iso), "MMM d, yyyy 'at' h:mm a"); } catch { return ""; }
  };

  return (
    <div className="space-y-2" data-testid="customer-files-tab">
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading files…
        </div>
      ) : files.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-theme-surface p-6 text-center text-sm text-muted-foreground">
          <Paperclip className="w-6 h-6 mx-auto mb-2 opacity-40" />
          <p>No files for this customer yet.</p>
          <p className="text-[11px] text-muted-foreground/70 mt-1">Upload photos or PDFs from any of this customer's jobs.</p>
        </div>
      ) : (
        files.map((f) => {
          const missing = isMissing(f);
          const openFile = () => {
            if (missing) return;
            if (isImage(f)) setLightboxAtt(f);
            else openAttachment(f);
          };
          return (
          <div
            key={f.attachment_id}
            className={`flex items-center gap-3 rounded-lg border border-border bg-theme-surface p-2.5 ${missing ? "opacity-70" : ""}`}
            data-testid={`customer-file-${f.attachment_id}`}
          >
            <button
              type="button"
              onClick={openFile}
              className="shrink-0"
              disabled={missing}
              data-testid={`customer-file-open-${f.attachment_id}`}
            >
              {missing ? (
                <div className="w-11 h-11 rounded-md bg-amber-500/10 flex items-center justify-center" title="File unavailable">
                  <Paperclip className="w-5 h-5 text-amber-500/70" />
                </div>
              ) : isImage(f) ? (
                <img src={attachmentUrl(f, { preview: true })} alt={f.file_name} className="w-11 h-11 rounded-md object-cover bg-muted" loading="lazy" />
              ) : (
                <div className="w-11 h-11 rounded-md bg-red-500/5 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-red-500/70" />
                </div>
              )}
            </button>
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={openFile}
                disabled={missing}
                className="text-sm font-medium text-foreground truncate hover:underline block max-w-full text-left disabled:no-underline disabled:cursor-default"
              >
                {f.file_name}
              </button>
              <p className="text-[11px] text-muted-foreground truncate">
                {missing ? "File unavailable · " : ""}
                {(f.ext || "").toUpperCase()}{f.ext ? " · " : ""}
                {formatFileSize(f.size)}{f.created_at ? ` · ${fmtDate(f.created_at)}` : ""}{f.uploaded_by_name ? ` · by ${f.uploaded_by_name}` : ""}
              </p>
            </div>
            {!missing && (
              <button
                type="button"
                onClick={() => downloadAttachment(f)}
                className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                title="Download"
                data-testid={`customer-file-download-${f.attachment_id}`}
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => onOpenJob?.({ job_id: f.job_id })}
              disabled={!onOpenJob}
              className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-[#0046BE]/10 text-[#0046BE] hover:bg-[#0046BE]/20 transition-colors max-w-[150px] disabled:opacity-70 disabled:cursor-default"
              data-testid={`customer-file-job-${f.attachment_id}`}
              title="Open job"
            >
              <Briefcase className="w-3 h-3 shrink-0" />
              <span className="truncate">{f.job_display_id || f.job_title || "Job"}</span>
            </button>
          </div>
          );
        })
      )}
      <AttachmentLightbox att={lightboxAtt} onClose={() => setLightboxAtt(null)} />
    </div>
  );
};

// ─── Tab: Activity ───────────────────────────────────────────────

const ActivityTab = ({ customer }) => {
  return (
    <div className="space-y-2" data-testid="customer-activity-tab">
      <ActivityRow
        icon={User}
        label="Customer added"
        value={customer?.created_at ? format(new Date(customer.created_at), "MMM d, yyyy 'at' h:mm a") : "—"}
      />
      <ActivityRow
        icon={Briefcase}
        label="Total jobs"
        value={`${customer?.total_jobs || 0} jobs`}
      />
      <ActivityRow
        icon={DollarSign}
        label="Lifetime value"
        value={`$${(customer?.total_spent || 0).toLocaleString()}`}
        accent="emerald"
      />
      {customer?.last_contact_date && (
        <ActivityRow
          icon={Clock}
          label="Last contact"
          value={format(new Date(customer.last_contact_date), "MMM d, yyyy")}
        />
      )}
      <ActivityRow
        icon={Phone}
        label="Phone"
        value={customer?.phone || "Not on file"}
      />
      {customer?.secondary_phone && (
        <ActivityRow
          icon={Phone}
          label="Secondary phone"
          value={customer.secondary_phone}
        />
      )}
      <ActivityRow
        icon={Mail}
        label="Email"
        value={customer?.email || "Not on file"}
      />
      <ActivityRow
        icon={Home}
        label="Primary address"
        value={
          customer?.address || customer?.city
            ? [customer?.address, customer?.city].filter(Boolean).join(", ")
            : "Not on file"
        }
      />
      {customer?.notes && (
        <ActivityRow
          icon={FileText}
          label="Notes"
          value={customer.notes}
          multiline
        />
      )}
    </div>
  );
};

const ActivityRow = ({ icon: Icon, label, value, accent, multiline }) => {
  const accentClasses = {
    emerald: "text-emerald-400 font-semibold",
  };
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-theme-surface p-3">
      <Icon className="w-4 h-4 text-muted-foreground/70 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-0.5">{label}</p>
        <p className={`text-sm ${multiline ? "whitespace-pre-wrap" : "truncate"} ${accentClasses[accent] || "text-foreground"}`}>
          {value}
        </p>
      </div>
    </div>
  );
};

export default CustomerModal;
